import React, { useRef, useEffect, useState } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import { Error as ErrorIcon, CheckCircle as CheckCircleIcon, Warning as WarningIcon, Info as InfoIcon, UnfoldMore as UnfoldMoreIcon } from "@mui/icons-material";
import { useAlertsProvider } from "../../../Providers/AlertsProvider";
import { useFeed } from "../../../Pages/Data/Visualize/DataReview/Hooks/useFeed";

export const AlertsStatusDisplay = ({ onViewAlertsClick, excludeAlertTitles = [], monitoringStatus, onExpandClick }) => {
    const textRef = useRef(null);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const { alerts, setSelectedAlertId } = useAlertsProvider();
    
    // Filter out silenced alerts, excluded alert titles, and sort by timestamp (latest first)
    const activeAlerts = alerts
        .filter(alert => !alert.silenced)
        .filter(alert => !excludeAlertTitles.includes(alert.title))
        .sort((a, b) => b.timestamp - a.timestamp);
    
    // Cycle through alerts every 3 seconds
    const currentAlert = useFeed(activeAlerts, { cycleTimeSeconds: 3 });
    
    // Check if we have multiple alerts to enable animation
    const hasMultipleAlerts = activeAlerts.length > 1;
    
    // Determine status message when no alerts
    const getStatusMessage = () => {
        if (monitoringStatus === "PAUSED") {
            return { title: "Monitoring paused", description: "Resume to continue monitoring" };
        } else if (monitoringStatus === "PAUSING") {
            return { title: "Monitoring pausing", description: "Please wait..." };
        }
        return { title: "Monitoring stable", description: "No issues detected" };
    };
    
    // Map severity to styling
    const getSeverityStyle = (severity) => {
        switch(severity) {
            case 'HIGH':
                return {
                    bg: 'rgba(211, 47, 47, 0.12)',
                    color: '#F44336',
                    icon: ErrorIcon,
                    label: 'Error'
                };
            case 'MEDIUM':
                return {
                    bg: 'rgba(255, 152, 0, 0.12)',
                    color: '#FF9800',
                    icon: WarningIcon,
                    label: 'Warning'
                };
            case 'LOW':
                return {
                    bg: 'rgba(33, 150, 243, 0.12)',
                    color: '#2196F3',
                    icon: InfoIcon,
                    label: 'Info'
                };
            default:
                // Check if monitoring is paused
                if (monitoringStatus === "PAUSED" || monitoringStatus === "PAUSING") {
                    return {
                        bg: 'rgba(255, 152, 0, 0.12)',
                        color: '#FF9800',
                        icon: WarningIcon,
                        label: 'Paused'
                    };
                }
                return {
                    bg: 'rgba(76, 175, 80, 0.12)',
                    color: '#4CAF50',
                    icon: CheckCircleIcon,
                    label: 'Stable'
                };
        }
    };
    
    // Determine what to display
    const style = currentAlert 
        ? getSeverityStyle(currentAlert.severity) 
        : getSeverityStyle(null);
    
    const StatusIcon = style.icon;
    const statusMessage = getStatusMessage();
    
    // Check for overflow
    useEffect(() => {
        if (textRef.current && currentAlert) {
            const element = textRef.current;
            const isTextOverflowing = element.scrollHeight > element.clientHeight;
            setIsOverflowing(isTextOverflowing);
        } else {
            setIsOverflowing(false);
        }
    }, [currentAlert]);
    
    return (
        <Box sx={{ 
            display: "flex", 
            alignItems: "center",
            gap: 2,
            padding: "12px 16px",
            borderRadius: "0px 0px 4px 4px",
            backgroundColor: style.bg,
            marginTop: 2,
            marginLeft: "-16px",
            marginRight: "-16px",
            marginBottom: "-16px",
            transition: "background-color 0.5s ease-in-out",
            height: "56px",
            overflow: "hidden"
        }}>
            <Box 
                key={currentAlert?.id || "stable"}
                sx={{ 
                    display: "flex", 
                    alignItems: "center", 
                    justifyContent: "flex-start",
                    gap: 1.5,
                    flex: "1",
                    minWidth: 0,
                    ...(hasMultipleAlerts && {
                        animation: "smoothFadeIn 0.6s ease-in-out",
                        "@keyframes smoothFadeIn": {
                            "0%": {
                                opacity: 0
                            },
                            "100%": {
                                opacity: 1
                            }
                        }
                    })
                }}
            >
                <StatusIcon sx={{ 
                    color: style.color, 
                    fontSize: "20px",
                    flexShrink: 0
                }} />
                <Box sx={{ 
                    minWidth: 0,
                    flex: 1
                }}>
                    <Typography ref={textRef} sx={{ 
                        fontSize: "14px",
                        color: style.color,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        lineHeight: "1.4"
                    }}>
                        {currentAlert ? currentAlert.title : statusMessage.title}
                        <Typography component="span" sx={{ 
                            fontSize: "12px",
                            color: "var(--text-secondary-color)",
                            marginLeft: 1
                        }}>
                            {currentAlert ? currentAlert.description : statusMessage.description}
                        </Typography>
                    </Typography>
                </Box>
            </Box>
            {isOverflowing && onExpandClick && currentAlert && (
                <Box sx={{ 
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    paddingLeft: "8px"
                }}>
                    <IconButton 
                        size="small"
                        onClick={() => onExpandClick(currentAlert)}
                        sx={{ 
                            color: style.color,
                            padding: "4px"
                        }}
                    >
                        <UnfoldMoreIcon sx={{ fontSize: "20px" }} />
                    </IconButton>
                </Box>
            )}
            {onViewAlertsClick && (
                <Box sx={{ 
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    paddingLeft: "24px"
                }}>
                    <Typography 
                        sx={{ 
                            fontSize: "14px",
                            color: "var(--color-accent-interactive)",
                            fontWeight: 500,
                            textDecoration: "underline",
                            cursor: "pointer",
                            whiteSpace: "nowrap"
                        }}
                        onClick={() => {
                            // Set the selected alert to either current alert or first alert
                            const alertToSelect = currentAlert || activeAlerts[0];
                            if (alertToSelect) {
                                setSelectedAlertId(alertToSelect.id);
                            }
                            onViewAlertsClick();
                        }}
                    >
                        View alerts
                    </Typography>
                </Box>
            )}
        </Box>
    );
};
